import "./MultiSelect.css";

import RCSelect, { Option } from "rc-select";
import { ReactNode, useEffect, useMemo, useState } from "react";

import { CheckedBoxBlueIcon, DropdownLightIcon, UncheckedBoxLightIcon } from "../../../assets";
import { SelectData } from "../../../models";
import { FormLabel } from "../FormLabel";
import { getDropdownPopupContainer } from "../utils/getDropdownPopupContainer";

/** Sentinel value for the clear all row — never part of `data`, so it can never be a real selection. */
const clearAllKey = "__MultiSelect_clearAll__";

export interface MultiSelectProps {
  label?: string;
  allowLabelDangerousHtml?: boolean;
  initialKey?: string[] | null;
  values?: string[] | null;
  placeholder?: string;
  className?: string;
  dropdownMatchSelectWidth?: boolean;
  data: SelectData;
  order?: string[];
  onChange: (values: string[]) => void;
  tooltip?: ReactNode;
  corners?: "default" | "rounded";
  hasError?: boolean;
  disabled?: boolean;
  showClearAll?: boolean;
  clearAllText?: string;
  /** Extra class on the (portaled) dropdown popup, for scoping styles to it. */
  dropdownClassName?: string;
  /** Narrows the options to what is typed in the box. Without it, typing filters nothing (the box in
   *  a multiple select always accepts text — this is what makes that text do something). */
  showSearch?: boolean;
}

export const MultiSelect = ({
  label,
  allowLabelDangerousHtml,
  initialKey,
  values,
  placeholder = "Please select",
  className,
  dropdownMatchSelectWidth,
  data,
  order,
  onChange,
  tooltip,
  corners = "default",
  hasError = false,
  disabled = false,
  showClearAll = false,
  clearAllText = "Clear all",
  dropdownClassName,
  showSearch = false,
}: MultiSelectProps): JSX.Element => {
  const [selectedValues, setSelectedValues] = useState(values);

  useEffect(() => {
    setSelectedValues(values);
  }, [values]);

  const onChangeInternal = (vals: string[]): void => {
    // Clicking the clear all row appends the sentinel; treat it as "deselect everything" instead of a selection.
    const nextValues = vals.includes(clearAllKey) ? [] : vals;

    setSelectedValues(nextValues);
    onChange(nextValues);
  };

  const orderedData = useMemo<SelectData>(() => {
    if (order) {
      const updatedData: SelectData = [];

      order?.forEach((ord) => {
        const option = data.find((d) => d.key === ord);

        if (option) {
          updatedData.push(option);
        }
      });

      return updatedData;
    }

    return data;
  }, [order, data]);

  const options = orderedData?.map((kvp) => (
    // `title` is the label on its own, which is what a search matches on — the children also carry
    // the checkbox element, so filtering over those would match on the icon's markup as well.
    <Option key={kvp.key} value={kvp.key} title={String(kvp.value ?? "")}>
      {selectedValues?.includes(kvp.key as string) ? <CheckedBoxBlueIcon /> : <UncheckedBoxLightIcon />}
      {kvp.value}
    </Option>
  ));

  /* Deliberately given no `title`: a search term then filters this row out along with the options it
     doesn't match, rather than leaving a clear-all floating above an unrelated result list. */
  const clearAllOption =
    showClearAll && !!selectedValues?.length ? (
      <Option key={clearAllKey} value={clearAllKey} className="MultiSelect_clearAll">
        {clearAllText}
      </Option>
    ) : null;

  return (
    <FormLabel label={label} tooltip={tooltip} tooltipJustify="left" allowDangerousHtml={allowLabelDangerousHtml}>
      <div
        className={`${className || ""} form_field ${corners} ${disabled ? "MultiSelectDisabled" : ""} ${hasError ? "MultiSelectHasError" : ""}`}
      >
        <RCSelect
          mode="multiple"
          defaultValue={initialKey}
          value={selectedValues}
          suffixIcon={<DropdownLightIcon />}
          placeholder={placeholder || undefined}
          onChange={onChangeInternal}
          prefixCls="MultiSelect"
          dropdownClassName={dropdownClassName}
          optionFilterProp="title"
          filterOption={showSearch ? undefined : false}
          dropdownMatchSelectWidth={dropdownMatchSelectWidth}
          getPopupContainer={getDropdownPopupContainer}
          dropdownAlign={{ overflow: { adjustY: 0 } }}
          removeIcon
          disabled={disabled}
        >
          {clearAllOption}
          {options}
        </RCSelect>
      </div>
    </FormLabel>
  );
};
